import jsPDF from 'jspdf';

export function exportToPDF({ question, answer, sources, decisions_cited, userEmail, lang }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  
  const pageW = 210;
  const pageH = 297;
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = margin;

  // Colors
  const gold = [201, 168, 76];
  const darkBlue = [8, 13, 26];
  const gray = [100, 100, 100];
  const lightGray = [240, 240, 240];

  // ── Header bar ──────────────────────────────────────────────────
  doc.setFillColor(...darkBlue);
  doc.rect(0, 0, pageW, 35, 'F');

  doc.setFillColor(...gold);
  doc.rect(0, 33, pageW, 2, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('OHADA Expert', margin, 15);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gold);
  doc.text('Intelligence Juridique IA  |  3 833 décisions CCJA  |  17 États membres', margin, 22);

  doc.setTextColor(180, 180, 180);
  doc.text(`ohada-fronted.vercel.app`, margin, 29);

  y = 45;

  // ── Memo header ──────────────────────────────────────────────────
  doc.setFillColor(...lightGray);
  doc.rect(margin, y, contentW, 28, 'F');
  doc.setDrawColor(...gold);
  doc.rect(margin, y, contentW, 28, 'S');

  doc.setTextColor(...darkBlue);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');

  const date = new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  doc.text(lang === 'fr' ? 'NOTE JURIDIQUE OHADA' : 'OHADA LEGAL MEMO', margin + 5, y + 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...gray);
  doc.text(`${lang === 'fr' ? 'Date' : 'Date'}: ${date}`, margin + 5, y + 15);
  doc.text(`${lang === 'fr' ? 'Généré par' : 'Generated by'}: OHADA Expert AI`, margin + 5, y + 21);
  doc.text(`${lang === 'fr' ? 'Utilisateur' : 'User'}: ${userEmail || 'N/A'}`, margin + 90, y + 15);
  doc.text(`${lang === 'fr' ? 'Décisions CCJA analysées' : 'CCJA decisions analyzed'}: ${decisions_cited}`, margin + 90, y + 21);

  y += 35;

  // ── Question ──────────────────────────────────────────────────
  doc.setFillColor(...gold);
  doc.rect(margin, y, 3, 10, 'F');
  doc.setTextColor(...darkBlue);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(lang === 'fr' ? 'QUESTION POSÉE' : 'QUESTION ASKED', margin + 6, y + 7);
  y += 14;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  const questionLines = doc.splitTextToSize(question, contentW);
  doc.text(questionLines, margin, y);
  y += questionLines.length * 5 + 8;

  // ── Divider ──────────────────────────────────────────────────
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + contentW, y);
  y += 8;

  // ── Answer ──────────────────────────────────────────────────
  doc.setFillColor(...gold);
  doc.rect(margin, y, 3, 10, 'F');
  doc.setTextColor(...darkBlue);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(lang === 'fr' ? 'ANALYSE JURIDIQUE' : 'LEGAL ANALYSIS', margin + 6, y + 7);
  y += 14;

  // Clean answer text for PDF
  const cleanAnswer = answer
    .replace(/🟢|🟡|🟠|🔴/g, '')
    .replace(/#{1,3} /g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/▸ /g, '• ')
    .replace(/⚖️|📋|💡|⚠️/g, '')
    .trim();

  const answerLines = doc.splitTextToSize(cleanAnswer, contentW);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(40, 40, 40);

  for (let i = 0; i < answerLines.length; i++) {
    if (y > pageH - 40) {
      doc.addPage();
      y = margin;
    }
    doc.text(answerLines[i], margin, y);
    y += 5;
  }

  y += 8;

  // ── Sources ──────────────────────────────────────────────────
  if (sources && sources.length > 0) {
    if (y > pageH - 60) { doc.addPage(); y = margin; }

    doc.setDrawColor(...gold);
    doc.line(margin, y, margin + contentW, y);
    y += 8;

    doc.setFillColor(...gold);
    doc.rect(margin, y, 3, 10, 'F');
    doc.setTextColor(...darkBlue);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(lang === 'fr' ? 'DÉCISIONS CCJA CITÉES' : 'CCJA DECISIONS CITED', margin + 6, y + 7);
    y += 14;

    sources.forEach((s, i) => {
      if (!s.url) return;
      if (y > pageH - 30) { doc.addPage(); y = margin; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...darkBlue);
      doc.text(`[${i + 1}] ${s.arret ? `Arrêt n° ${s.arret}` : 'Décision CCJA'}${s.date ? ` du ${s.date}` : ''}`, margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 200);
      doc.textWithLink(s.url, margin, y, { url: s.url });
      y += 7;
    });
  }

  // ── Footer ──────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(...darkBlue);
    doc.rect(0, pageH - 15, pageW, 15, 'F');
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(
      lang === 'fr'
        ? '⚠️ Ce document est généré par une IA à titre informatif. Il ne constitue pas un avis juridique. Consultez un avocat qualifié.'
        : '⚠️ This document is AI-generated for informational purposes only. It does not constitute legal advice.',
      margin, pageH - 8
    );
    doc.setTextColor(...gold);
    doc.text(`Page ${p}/${totalPages}`, pageW - margin, pageH - 8, { align: 'right' });
  }

  // Save
  const filename = `OHADA-Expert-${new Date().toISOString().slice(0,10)}.pdf`;
  doc.save(filename);
}
